import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, Input, Button, Form, Tag, message } from "antd";
import { LockOutlined, MailOutlined, ShopOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { completeUserRegistrationMutation } from "../../services/client/@tanstack/react-query.gen";
import { extractErrorMessage } from "../../util/extractErrorMessage";
import {
  identityTypeColors,
  identityTypeLabels,
  type IdentityType,
} from "../../util/identityType";

type RegisterConfirmFormValues = {
  password: string;
  confirmPassword: string;
};

const isIdentityType = (value: string | null): value is IdentityType =>
  value === "user" || value === "site_owner" || value === "admin";

const RegisterConfirmPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const partner = searchParams.get("partner");
  const sites = (searchParams.get("sites") ?? "")
    .split(",")
    .filter((site) => site.length > 0);
  const typeParam = searchParams.get("type");
  const accountType = isIdentityType(typeParam) ? typeParam : null;

  const completeRegistration = useMutation({
    ...completeUserRegistrationMutation(),
  });

  const onFinish = (values: RegisterConfirmFormValues) => {
    if (!token || !email) {
      return;
    }
    setLoading(true);
    completeRegistration.mutate(
      { body: { token, email, password: values.password } },
      {
        onSuccess: () => {
          message.success("Account created! Please log in.");
          navigate("/login");
        },
        onError: (registrationError) => {
          setLoading(false);
          message.error(extractErrorMessage(registrationError));
        },
      },
    );
  };

  if (!token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="w-full max-w-md p-6 bg-white rounded shadow-md">
          <Alert
            type="error"
            showIcon
            message="Invalid invite link"
            description="This link is missing its invite token or email. Please use the link from your invitation email."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md p-6 bg-white rounded shadow-md">
        <h2
          className={`text-center text-2xl font-bold ${accountType ? "mb-1" : "mb-4"}`}
        >
          Create your account
        </h2>
        {accountType && (
          <div className="text-center mb-4">
            <Tag color={identityTypeColors[accountType]}>
              {identityTypeLabels[accountType]}
            </Tag>
          </div>
        )}
        <Form<RegisterConfirmFormValues>
          name="register-confirm"
          onFinish={onFinish}
          layout="vertical"
        >
          <Form.Item label="Email">
            <Input
              prefix={<MailOutlined className="text-gray-400" />}
              value={email}
              disabled
              size="large"
            />
          </Form.Item>

          {partner && (
            <Form.Item label="Partner">
              <Input
                prefix={<ShopOutlined className="text-gray-400" />}
                value={partner}
                disabled
                size="large"
              />
            </Form.Item>
          )}

          {sites.length > 0 && (
            <Form.Item label="Sites">
              <div className="flex flex-wrap gap-1">
                {sites.map((site) => (
                  <Tag key={site}>{site}</Tag>
                ))}
              </div>
            </Form.Item>
          )}

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Please input a password!" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-400" />}
              placeholder="Password"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password!" },
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match."));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-400" />}
              placeholder="Confirm password"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              size="large"
              loading={loading}
            >
              Create account
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default RegisterConfirmPage;
